import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  LinearProgress,
  Fade,
  Backdrop,
  Portal,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

const POSITION_OFFSETS = {
  top: { vertical: 'top', horizontal: 'center', offsetY: -16 },
  bottom: { vertical: 'bottom', horizontal: 'center', offsetY: 16 },
  left: { vertical: 'center', horizontal: 'left', offsetX: -16 },
  right: { vertical: 'center', horizontal: 'right', offsetX: 16 },
  'top-left': { vertical: 'top', horizontal: 'left', offsetY: -16, offsetX: -16 },
  'top-right': { vertical: 'top', horizontal: 'right', offsetY: -16, offsetX: 16 },
  'bottom-left': { vertical: 'bottom', horizontal: 'left', offsetY: 16, offsetX: -16 },
  'bottom-right': { vertical: 'bottom', horizontal: 'right', offsetY: 16, offsetX: 16 },
};

const OnboardingTour = ({
  tour,
  isOpen,
  onClose,
  onStepComplete,
  onTourComplete,
  theme,
  initialStepIndex = 0,
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(initialStepIndex);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [highlightRect, setHighlightRect] = useState(null);
  const [isElementReady, setIsElementReady] = useState(false);
  const tooltipRef = useRef(null);
  const observerRef = useRef(null);

  const currentStep = tour?.steps?.[currentStepIndex];
  const totalSteps = tour?.steps?.length || 0;
  const progress = totalSteps > 0 ? ((currentStepIndex + 1) / totalSteps) * 100 : 0;

  const themeStyles = {
    primaryColor: theme?.primary_color || '#667eea',
    secondaryColor: theme?.secondary_color || '#764ba2',
    backgroundColor: theme?.background_color || '#ffffff',
    textColor: theme?.text_color || '#333333',
    borderRadius: theme?.border_radius || 8,
    overlayColor: theme?.overlay_color || 'rgba(0, 0, 0, 0.5)',
    highlightColor: theme?.highlight_color || 'rgba(102, 126, 234, 0.3)',
  };

  const calculatePosition = useCallback((targetElement, position) => {
    if (!targetElement) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

    const rect = targetElement.getBoundingClientRect();
    const posConfig = POSITION_OFFSETS[position] || POSITION_OFFSETS.bottom;
    const padding = currentStep?.highlight_padding || 8;

    let top, left;

    switch (posConfig.vertical) {
      case 'top':
        top = rect.top - padding + (posConfig.offsetY || 0);
        break;
      case 'bottom':
        top = rect.bottom + padding + (posConfig.offsetY || 0);
        break;
      default:
        top = rect.top + rect.height / 2;
    }

    switch (posConfig.horizontal) {
      case 'left':
        left = rect.left - padding + (posConfig.offsetX || 0);
        break;
      case 'right':
        left = rect.right + padding + (posConfig.offsetX || 0);
        break;
      default:
        left = rect.left + rect.width / 2;
    }

    return { top, left };
  }, [currentStep?.highlight_padding]);

  const updatePositions = useCallback(() => {
    if (!currentStep) return;

    if (currentStep.step_type === 'modal' || !currentStep.target_selector) {
      setHighlightRect(null);
      setTooltipPosition({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
      setIsElementReady(true);
      return;
    }

    const targetElement = document.querySelector(currentStep.target_selector);
    
    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      const padding = currentStep.highlight_padding || 8;
      
      setHighlightRect({
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
      });

      const pos = calculatePosition(targetElement, currentStep.target_position);
      setTooltipPosition(pos);
      setIsElementReady(true);

      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else if (currentStep.wait_for_element) {
      setIsElementReady(false);
      
      const timeout = (currentStep.element_timeout_seconds || 10) * 1000;
      const startTime = Date.now();

      const checkElement = () => {
        const element = document.querySelector(currentStep.target_selector);
        if (element) {
          updatePositions();
        } else if (Date.now() - startTime < timeout) {
          requestAnimationFrame(checkElement);
        } else {
          setHighlightRect(null);
          setTooltipPosition({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
          setIsElementReady(true);
        }
      };
      
      requestAnimationFrame(checkElement);
    } else {
      setHighlightRect(null);
      setTooltipPosition({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
      setIsElementReady(true);
    }
  }, [currentStep, calculatePosition]);

  useEffect(() => {
    if (!isOpen || !currentStep) return;

    updatePositions();

    const handleResize = () => updatePositions();
    const handleScroll = () => updatePositions();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, currentStep, updatePositions]);

  useEffect(() => {
    if (!isOpen) return;

    observerRef.current = new MutationObserver(() => {
      updatePositions();
    });

    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [isOpen, updatePositions]);

  const handleNext = useCallback(() => {
    if (currentStep && onStepComplete) {
      onStepComplete(currentStep.id, currentStepIndex);
    }

    if (currentStepIndex < totalSteps - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      if (onTourComplete) {
        onTourComplete(tour.id);
      }
      onClose();
    }
  }, [currentStep, currentStepIndex, totalSteps, tour?.id, onStepComplete, onTourComplete, onClose]);

  const handlePrevious = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  }, [currentStepIndex]);

  const handleSkip = useCallback(() => {
    if (currentStep?.allow_skip !== false) {
      handleNext();
    }
  }, [currentStep?.allow_skip, handleNext]);

  const handleClose = useCallback(() => {
    onClose('user_closed');
  }, [onClose]);

  if (!isOpen || !tour || !currentStep) return null;

  const isModal = currentStep.step_type === 'modal';
  const isLastStep = currentStepIndex === totalSteps - 1;

  return (
    <Portal>
      <Backdrop
        open={isOpen}
        sx={{
          zIndex: 1300,
          backgroundColor: themeStyles.overlayColor,
        }}
        onClick={handleClose}
      />

      {highlightRect && !isModal && (
        <Box
          sx={{
            position: 'fixed',
            top: highlightRect.top,
            left: highlightRect.left,
            width: highlightRect.width,
            height: highlightRect.height,
            borderRadius: `${themeStyles.borderRadius}px`,
            boxShadow: `0 0 0 9999px ${themeStyles.overlayColor}`,
            backgroundColor: themeStyles.highlightColor,
            zIndex: 1301,
            pointerEvents: 'none',
            transition: 'all 0.3s ease-in-out',
          }}
        />
      )}

      <Fade in={isOpen && isElementReady}>
        <Paper
          ref={tooltipRef}
          elevation={8}
          sx={{
            position: 'fixed',
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            transform: tooltipPosition.transform || 'translate(-50%, 0)',
            zIndex: 1302,
            maxWidth: isModal ? 500 : 400,
            minWidth: 280,
            borderRadius: `${themeStyles.borderRadius}px`,
            backgroundColor: themeStyles.backgroundColor,
            overflow: 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {currentStep.show_progress !== false && (
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 4,
                backgroundColor: 'rgba(0,0,0,0.1)',
                '& .MuiLinearProgress-bar': {
                  background: `linear-gradient(90deg, ${themeStyles.primaryColor}, ${themeStyles.secondaryColor})`,
                },
              }}
            />
          )}

          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: themeStyles.textColor,
                  flex: 1,
                }}
              >
                {currentStep.title}
              </Typography>
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{ ml: 1, color: 'text.secondary' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mb: 3,
                lineHeight: 1.6,
              }}
            >
              {currentStep.content}
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                {currentStepIndex + 1} / {totalSteps}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1 }}>
                {currentStepIndex > 0 && (
                  <Button
                    size="small"
                    onClick={handlePrevious}
                    startIcon={<ArrowBackIcon />}
                    sx={{ color: themeStyles.primaryColor }}
                  >
                    Back
                  </Button>
                )}

                {currentStep.allow_skip !== false && !isLastStep && (
                  <Button
                    size="small"
                    onClick={handleSkip}
                    sx={{ color: 'text.secondary' }}
                  >
                    Skip
                  </Button>
                )}

                <Button
                  variant="contained"
                  size="small"
                  onClick={handleNext}
                  endIcon={isLastStep ? <CheckCircleIcon /> : <ArrowForwardIcon />}
                  sx={{
                    background: `linear-gradient(90deg, ${themeStyles.primaryColor}, ${themeStyles.secondaryColor})`,
                    '&:hover': {
                      background: `linear-gradient(90deg, ${themeStyles.secondaryColor}, ${themeStyles.primaryColor})`,
                    },
                  }}
                >
                  {isLastStep ? 'Finish' : 'Next'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Fade>
    </Portal>
  );
};

OnboardingTour.propTypes = {
  tour: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    steps: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        content: PropTypes.string.isRequired,
        step_type: PropTypes.oneOf(['tooltip', 'modal', 'highlight', 'beacon']),
        target_selector: PropTypes.string,
        target_position: PropTypes.oneOf([
          'top', 'bottom', 'left', 'right',
          'top-left', 'top-right', 'bottom-left', 'bottom-right',
        ]),
        highlight_padding: PropTypes.number,
        allow_skip: PropTypes.bool,
        show_progress: PropTypes.bool,
      })
    ).isRequired,
  }),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onStepComplete: PropTypes.func,
  onTourComplete: PropTypes.func,
  theme: PropTypes.shape({
    primary_color: PropTypes.string,
    secondary_color: PropTypes.string,
    background_color: PropTypes.string,
    text_color: PropTypes.string,
    border_radius: PropTypes.number,
    overlay_color: PropTypes.string,
    highlight_color: PropTypes.string,
  }),
  initialStepIndex: PropTypes.number,
};

export default React.memo(OnboardingTour);
